import { ActionSheetProvider } from "@expo/react-native-action-sheet";
import { getFocusedRouteNameFromRoute } from "@react-navigation/native";
import { Redirect, Tabs, usePathname, useRouter } from "expo-router";
import React, { useEffect, useState } from "react";
import { BackHandler, Platform, View } from "react-native";

import { CustomHeader } from "@/components/CustomHeader";
import { HapticTab } from "@/components/HapticTab";
import Sidebar from "@/components/Sidebar";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import TabBarBackground from "@/components/ui/TabBarBackground";
import { resolveHomeTabScreen } from "@/constants/AppNavigation";
import { Colors } from "@/constants/Colors";
import { useAuth } from "@/context/AuthContext";
import { PostsProvider } from "@/context/PostsContext";
import { ToastProvider } from "@/context/ToastContext";
import { useColorScheme } from "@/hooks/useColorScheme";
import Feather from "@expo/vector-icons/Feather";
import { getApp } from "firebase/app";
import {
  collection,
  doc,
  getFirestore,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { SafeAreaView } from "react-native-safe-area-context";

const firestore = getFirestore(getApp());

const UnreadBadge = ({ count }: { count: number }) => {
  if (count === 0) return null;
  return (
    <ThemedView
      style={{
        position: "absolute",
        right: -5,
        top: -5,
        backgroundColor: "red",
        borderRadius: 10,
        width: 20,
        height: 20,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <ThemedText style={{ color: "white", fontSize: 12 }}>{count}</ThemedText>
    </ThemedView>
  );
};

export default function TabLayout() {
  const { user, userData, isLoading } = useAuth();
  const { t } = useTranslation();
  const colorScheme = useColorScheme();
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [groupInvitationCount, setGroupInvitationCount] = useState(0);

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (user) {
      const notificationsRef = collection(
        doc(collection(firestore, "users"), user.uid),
        "notifications",
      );
      const q = query(notificationsRef, where("read", "==", false));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setUnreadCount(snapshot.size);
        },
        (error) => {
          console.error("Notifications listener permission error:", error);
          setUnreadCount(0);
        },
      );
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const groupInvitationsRef = collection(firestore, "groupInvitations");
      const q = query(
        groupInvitationsRef,
        where("inviteeUid", "==", user.uid),
        where("status", "==", "pending"),
      );
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          setGroupInvitationCount(snapshot.size);
        },
        (error) => {
          console.error(
            "Group invitation badge listener permission error:",
            error,
          );
          setGroupInvitationCount(0);
        },
      );
      return () => unsubscribe();
    }
  }, [user]);

  useEffect(() => {
    if (Platform.OS === "android") {
      const onBackPress = () => {
        if (pathname !== "/") {
          router.replace("/");
          return true;
        }
        return false;
      };
      const subscription = BackHandler.addEventListener(
        "hardwareBackPress",
        onBackPress,
      );
      return () => subscription.remove();
    }
  }, [pathname, router]);

  if (isLoading) {
    return null;
  }

  // Wait for the Firestore user doc before mounting the Tabs navigator so
  // `initialRouteName` is picked from the user's defaultHomeTab preference,
  // not the "feed" fallback.
  if (user && !userData) {
    return null;
  }

  if (!user) {
    return <Redirect href="/(auth)/login" />;
  }

  return (
    <ActionSheetProvider>
      <ToastProvider>
        <PostsProvider>
          <SafeAreaView style={{ flex: 1 }} edges={["top", "bottom"]}>
            <Tabs
              initialRouteName={resolveHomeTabScreen(userData?.defaultHomeTab)}
              screenOptions={{
                tabBarShowLabel: false,
                tabBarActiveTintColor:
                  Colors[colorScheme ?? "light"].tabIconActive,
                tabBarInactiveTintColor:
                  Colors[colorScheme ?? "light"].tabIconInactive,
                headerShown: false,
                tabBarButton: HapticTab,
                tabBarBackground: TabBarBackground,
                tabBarItemStyle: {
                  paddingBottom: 6,
                  paddingTop: 2,
                },
                tabBarStyle: {
                  height: 40,
                  borderTopWidth: 0,
                  backgroundColor:
                    Colors[colorScheme ?? "light"].tabBarBackground,
                  ...(Platform.OS === "ios" && {
                    position: "absolute",
                  }),
                },
              }}
              backBehavior="history"
            >
              <Tabs.Screen
                name="profile"
                options={{
                  tabBarIcon: ({ color }) => (
                    <Feather size={24} name="user" color={color} />
                  ),
                  tabBarStyle: {
                    display: "none",
                  },
                }}
                listeners={{
                  tabPress: (e: any) => {
                    e.preventDefault();
                    setSidebarVisible(true);
                  },
                }}
              />
              <Tabs.Screen
                name="groups"
                options={({ route }) => {
                  // Hide the bottom tabs on groups sub-flows (`group-info`,
                  // `create`, `[id]`, `invite`). Any other value must OMIT
                  // the tabBarStyle key entirely — returning `undefined`
                  // here would override the parent screenOptions' custom
                  // (position:absolute) style and leave a blank gap at the
                  // bottom of the groups list.
                  const hide = [
                    "group-info",
                    "create",
                    "[id]",
                    "invite",
                  ].includes(getFocusedRouteNameFromRoute(route) ?? "");
                  return {
                    tabBarIcon: ({ color }) => (
                      <View>
                        <Feather size={24} name="users" color={color} />
                        <UnreadBadge count={groupInvitationCount} />
                      </View>
                    ),
                    ...(hide ? { tabBarStyle: { display: "none" } } : {}),
                  };
                }}
                listeners={{
                  tabPress: (e: any) => {
                    e.preventDefault();
                    router.replace("/(tabs)/groups");
                  },
                }}
              />
              <Tabs.Screen
                name="index"
                options={{
                  tabBarIcon: ({ color }) => (
                    <Feather size={24} name="globe" color={color} />
                  ),
                }}
              />
              <Tabs.Screen
                name="notifications"
                options={{
                  headerShown: true,
                  header: () => (
                    <CustomHeader title={t("NOTIFICATIONS")} type="subtitle" />
                  ),
                  tabBarIcon: ({ color }) => (
                    <View>
                      <Feather size={24} name="bell" color={color} />
                      <UnreadBadge count={unreadCount} />
                    </View>
                  ),
                }}
              />
              <Tabs.Screen
                name="settings"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-plates"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-notifications"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-signin-methods"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="settings-default-home"
                options={{
                  href: null,
                  tabBarItemStyle: {
                    display: "none",
                  },
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="help"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="post/[postId]"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                  headerShown: false,
                }}
              />
              <Tabs.Screen
                name="post/new"
                options={{
                  title: "Post",
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="post/edit/[postId]"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                  headerShown: false,
                }}
              />
              <Tabs.Screen
                name="report/area"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="public-profile"
                options={{
                  href: null,
                  headerShown: false,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
              <Tabs.Screen
                name="search"
                options={{
                  href: null,
                  tabBarStyle: {
                    display: "none",
                  },
                }}
              />
            </Tabs>
            <Sidebar
              isVisible={isSidebarVisible}
              onClose={() => setSidebarVisible(false)}
            />
          </SafeAreaView>
        </PostsProvider>
      </ToastProvider>
    </ActionSheetProvider>
  );
}
